/**
 * 基本設計のレビューを画面で扱うための小さな読み方（状態の色・誰が提出できるか・チェック
 * リストの下書き）。
 *
 * 状態の移り方・提出の可否・理由が要るかの最終判定は API が持つ（MCP の submit_bd_review と
 * 同じ口）。ここが持つのは、押しても API に断られると分かっているボタンを先に止めておく程度の
 * 画面の判断だけ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計のレビューの状態の色・指名されたレビュアーかどうか・チェックリストの下書きと承認できるかを決める
 * @graph-connects none
 */

import type { BdTone } from './bd-presentation.js';
import type {
  BdChecklistEntry,
  BdChecklistResult,
  BdFindingInput,
  BdFindingSeverity,
  BdReview,
  BdReviewPerspective,
  BdReviewStatus,
} from '../../../lib/api/basic-design.js';

/** 状態の色。直してほしいが返ってきた状態は目立たせる。 @graph-connects none */
const STATUS_TONES: Readonly<Record<BdReviewStatus, BdTone>> = {
  requested: 'info',
  ai_changes_requested: 'warning',
  ai_approved: 'info',
  reviewer_changes_requested: 'warning',
  approved: 'success',
};

/** 状態の色。 @graph-connects none */
export function reviewStatusTone(status: BdReviewStatus): BdTone {
  return STATUS_TONES[status];
}

/** 重さの色。 @graph-connects none */
export function severityTone(severity: BdFindingSeverity): BdTone {
  if (severity === 'critical') return 'error';
  return severity === 'major' ? 'warning' : 'default';
}

/**
 * 見ている人が指名されたレビュアーか。画面にはレビュアーのメールが来ないので、API がメールで
 * 照らした `callerIsReviewer` をそのまま使う。
 * @graph-connects none
 */
export function isDesignatedReviewer(
  review: Pick<BdReview, 'callerIsReviewer'> | undefined,
): boolean {
  return review?.callerIsReviewer === true;
}

/** レビュアーが段階を出せる状態か（AI の段階を通った後）。 @graph-connects none */
export function reviewerCanSubmit(status: BdReviewStatus | null): boolean {
  return status === 'ai_approved' || status === 'reviewer_changes_requested';
}

/** チェックリストの 1 観点の下書き。答えが未選択なら空文字。 @graph-connects none */
export interface BdChecklistDraft {
  perspective: string;
  result: BdChecklistResult | '';
  evidence: string;
}

/**
 * 観点ごとの下書きを作る。AI が先に埋めた答えがあればそれを初期値にする（レビュアーは確かめて
 * 直すだけでよい）。
 * @graph-connects none
 */
export function checklistDraftOf(
  perspectives: readonly BdReviewPerspective[],
  checklist: readonly BdChecklistEntry[],
): BdChecklistDraft[] {
  return perspectives.map((p) => {
    const entry = checklist.filter((c) => c.perspective === p.key).pop();
    return {
      perspective: p.key,
      result: entry?.result ?? '',
      evidence: entry?.evidence ?? '',
    };
  });
}

/** 新しく足す指摘の下書き。 @graph-connects none */
export type BdFindingDraft = BdFindingInput;

/** 承認・差し戻しの押せる状態。 @graph-connects none */
export interface BdReviewVerdict {
  /** すべての観点に答えた */
  complete: boolean;
  /** 承認できる（NG も重い指摘も無い） */
  canApprove: boolean;
  /** 差し戻せる（NG か指摘が 1 つ以上ある） */
  canRequestChanges: boolean;
}

/**
 * 承認と差し戻しのどちらが押せるか。どちらを押しても API が答えと指摘から結果を決めるので、
 * 結果と違う方のボタンは止めておく（承認を押したのに差し戻しになった、を起こさない）。
 * 書きかけ（要約が空）の指摘は数えない。
 * @graph-connects none
 */
export function reviewVerdict(
  draft: readonly BdChecklistDraft[],
  findings: readonly BdFindingDraft[],
  hasBlockingOpen: boolean,
): BdReviewVerdict {
  const complete = draft.every((d) => d.result !== '');
  const written = findings.filter((f) => f.summary.trim() !== '');
  const ng = draft.some((d) => d.result === 'ng');
  const blocking = hasBlockingOpen || written.some((f) => f.severity !== 'minor');
  return {
    complete,
    canApprove: complete && !ng && !blocking,
    canRequestChanges: complete && (ng || written.length > 0 || hasBlockingOpen),
  };
}

/** 未解決の重い指摘（承認を止めるもの）があるか。 @graph-connects none */
export function hasBlockingOpenFindings(review: Pick<BdReview, 'findings'>): boolean {
  return review.findings.some((f) => f.resolution === 'open' && f.severity !== 'minor');
}
